import { Vector2 } from "../../../../common/vector2.js";
import { resolveUiBackend } from "../../../dom_backend/dom_backend.js";
import { createNullCanvasContext } from "../../../dom_backend/null_canvas_context.js";
import { Game } from "../../../../game.js";
import { ColorScheme } from "../../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../../common_ui_elements/ui_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../../common_ui_elements/ui_text_element.js";
import { Easing } from "../../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../../transforms/transform_opacity.js";
import { TransformStraightMove } from "../../../../transforms/transform_straight_move.js";

export class InvalidShotMessage extends UIElement
{
  private static readonly MOVE_UP_DESIGN = 40;
  private static readonly MARGIN_DESIGN = 70;
  private static readonly MOVE_DURATION_MS = 1250;
  private static readonly FADE_IN_MS = 100;
  private static readonly FADE_OUT_START_RATIO = 0.6;

  private readonly canvas: HTMLCanvasElement;
  private text?: UITextElement;

  constructor( content_width: number, content_height: number )
  {
    const canvas = document.getElementById( "invalidShotCanvas" ) as HTMLCanvasElement;
    const real_ctx = canvas.getContext( "2d" );

    if ( !real_ctx )
      throw new Error( "invalidShotCanvas has no 2d context" );

    const ctx = resolveUiBackend( "game" ) === "dom" ? createNullCanvasContext( real_ctx ) : real_ctx;
    super( ctx, 0, 0, content_width, content_height, false, false );

    this.canvas = canvas;
    this.setClickable( false );
    this.mountDomHost( canvas );
  }

  public isShowing(): boolean
  {
    return this.text !== undefined;
  }

  public show(
    text_x: number,
    text_y: number,
    width: number,
    height: number
  ): void
  {
    this.hide();

    const move_up = LayoutScaler.heightFromDesign( InvalidShotMessage.MOVE_UP_DESIGN );
    const margin = LayoutScaler.heightFromDesign( InvalidShotMessage.MARGIN_DESIGN );

    const top = Math.max( 0, Math.round( Game.safeAreaTop() + text_y - move_up - margin ) );
    const css_height = Math.min(
      Game.getScreenHeight() - top,
      Math.round( height + move_up + margin * 2 )
    );

    this.placeCanvas( top, css_height );

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      width,
      height,
      {
        localization_key: "INVALID_SHOT_MESSAGE",
        font_size: LayoutScaler.textSizeFromDesign( 46 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        auto_wrap: true,
      }
    );

    const now = performance.now();
    const fade_out_start = now + InvalidShotMessage.MOVE_DURATION_MS * InvalidShotMessage.FADE_OUT_START_RATIO;
    const fade_out_duration = InvalidShotMessage.MOVE_DURATION_MS * ( 1 - InvalidShotMessage.FADE_OUT_START_RATIO );

    text.setOpacity( 0 );
    text.fadeIn( InvalidShotMessage.FADE_IN_MS, now, Easing.EaseOutQuad );

    const fade_out = new TransformOpacity(
      text,
      true,
      1,
      0,
      fade_out_start,
      fade_out_duration,
      { easing: Easing.EaseInQuad }
    );

    fade_out.setOnFinished( () =>
    {
      if ( this.text !== text )
        return;

      this.hide();
    } );

    text.addTransform( fade_out );

    text.addTransform( new TransformStraightMove(
      text,
      true,
      null,
      new Vector2( text_x, Math.round( text_y - move_up ) ),
      now,
      InvalidShotMessage.MOVE_DURATION_MS,
      { easing: Easing.EaseOutQuad }
    ) );

    this.text = text;
    this.addChild( text );
    this.canvas.style.display = "block";
    this.forceRedraw();
  }

  public hide(): void
  {
    if ( !this.text )
      return;

    this.removeChild( this.text );
    this.text = undefined;
    this.canvas.style.display = "none";
    this.skipRenderSubtree();
  }

  public update( now: number ): void
  {
    if ( !this.text )
      return;

    this.updateBasic( now );
  }

  public render(): void
  {
    if ( !this.text || !this.shouldBeRendered() )
      return;

    const ctx = this.ctx;

    ctx.save();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, this.canvas.width, this.canvas.height );
    ctx.restore();

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "InvalidShotMessage";
  }

  private placeCanvas( top: number, css_height: number ): void
  {
    const dpr = Game.getDpr();
    const css_width = Game.getScreenWidth();

    const target_w = Math.round( dpr * css_width );
    const target_h = Math.round( dpr * css_height );

    if ( this.canvas.width !== target_w )
      this.canvas.width = target_w;

    if ( this.canvas.height !== target_h )
      this.canvas.height = target_h;

    this.canvas.style.zIndex = "2";
    this.canvas.style.pointerEvents = "none";
    this.canvas.style.left = `${Game.instance.getGameX()}px`;
    this.canvas.style.top = `${top}px`;
    this.canvas.style.width = `${css_width}px`;
    this.canvas.style.height = `${css_height}px`;

    this.ctx.setTransform( 1, 0, 0, 1, 0, 0 );

    if ( dpr !== 1 )
      this.ctx.scale( dpr, dpr );

    this.ctx.translate( Game.safeAreaLeft(), Game.safeAreaTop() - top );
    this.setDomOriginOffset( Game.safeAreaLeft(), Game.safeAreaTop() - top );
  }
}
